import { VARIABLE_PILL_ATTR } from "@/shared/utils/variable-pill";

interface CommitDateTokenArgs {
  /** The clicked date pill, live in the editable. */
  pill: HTMLElement | null | undefined;
  /** The rewritten token, e.g. `kickoff+5d|D MMM YYYY`. */
  nextToken: string;
  /** Version mode re-resolves the pill's text from data; authoring shows the raw token. */
  isVersion: boolean;
  /** Ask the editor to commit its content (Froala's `save.save()`). */
  save: () => void;
}

/**
 * Rewrite a date pill's token in the LIVE editable and persist the change.
 *
 * The token is mutated straight on the DOM node — no editor command runs — so
 * Froala fires none of its own events for it. That matters for persistence:
 * the save is driven by those events (`contentChanged` starts the save timer,
 * `save.before` marks the content dirty), so a programmatic edit has to ask
 * for the save itself, exactly as the toolbar's variable insert does
 * (`toolbar-handlers/insert-handler.ts`). Reaching for the content serializer
 * directly instead skipped the dirty flag it checks first and returned early,
 * so adding an offset (`{{kickoff}}` → `{{kickoff+5d}}`) changed the pill on
 * screen but never autosaved — reload and the offset was gone.
 *
 * Returns whether anything changed, so a no-op re-pick costs no save.
 */
export const commitDateTokenChange = ({
  pill,
  nextToken,
  isVersion,
  save,
}: CommitDateTokenArgs): boolean => {
  if (!pill || pill.getAttribute(VARIABLE_PILL_ATTR) === nextToken)
    return false;
  pill.setAttribute(VARIABLE_PILL_ATTR, nextToken);
  if (!isVersion) pill.textContent = `{{${nextToken}}}`;
  save();
  return true;
};
